import { useState } from "react";
import { Award, Check, Hourglass, Plus, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { CREDENTIAL_CATALOG, type CredentialDef } from "@/lib/credentials";
import { useCompanyCredentials, type CredentialRow } from "@/hooks/useCompanyCredentials";

interface Props {
  companyProfileId: string;
}

const CredentialPill = ({
  def,
  row,
  onSet,
}: {
  def: CredentialDef;
  row: CredentialRow | undefined;
  onSet: (status: "earned" | "in_progress" | null) => void | Promise<unknown>;
}) => {
  const [open, setOpen] = useState(false);
  const Icon = def.icon;
  const status = row?.status;
  const base =
    "flex items-center gap-2 px-3 py-1.5 rounded-full text-xs border transition cursor-pointer max-w-full";
  const variant =
    status === "earned"
      ? "bg-primary/15 text-primary border-primary/40 hover:bg-primary/20"
      : status === "in_progress"
        ? "bg-card border-primary/30 text-foreground hover:border-primary/60"
        : "bg-background/40 border-border text-muted-foreground hover:text-foreground hover:border-primary/30";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={`${base} ${variant}`}>
          {status === "earned" ? (
            <Check className="h-3 w-3 shrink-0" />
          ) : status === "in_progress" ? (
            <Hourglass className="h-3 w-3 shrink-0" />
          ) : (
            <Plus className="h-3 w-3 shrink-0 opacity-60" />
          )}
          <Icon className="h-3.5 w-3.5 shrink-0" />
          <span className="font-medium truncate">{def.label}</span>
          {row?.earned_on && status === "earned" && (
            <span className="text-[10px] text-primary/70 ml-1 shrink-0">
              {new Date(row.earned_on).getFullYear()}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="start">
        <div className="flex items-start gap-2 mb-3">
          <Icon className="h-4 w-4 text-primary mt-0.5" />
          <div className="min-w-0">
            <div className="font-semibold text-sm">{def.label}</div>
            <div className="text-xs text-muted-foreground">{def.blurb}</div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground mt-1">
              Tier {def.tier}
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <Button
            size="sm"
            variant={status === "earned" ? "default" : "outline"}
            className="justify-start h-8"
            onClick={async () => {
              await onSet("earned");
              setOpen(false);
            }}
          >
            <Check className="h-3.5 w-3.5 mr-1.5" /> Mark earned
          </Button>
          <Button
            size="sm"
            variant={status === "in_progress" ? "secondary" : "outline"}
            className="justify-start h-8"
            onClick={async () => {
              await onSet("in_progress");
              setOpen(false);
            }}
          >
            <Hourglass className="h-3.5 w-3.5 mr-1.5" /> In progress
          </Button>
          {status && (
            <Button
              size="sm"
              variant="ghost"
              className="justify-start h-8 text-muted-foreground"
              onClick={async () => {
                await onSet(null);
                setOpen(false);
              }}
            >
              <X className="h-3.5 w-3.5 mr-1.5" /> Clear
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const CredentialsCard = ({ companyProfileId }: Props) => {
  const { byKey, setStatus, earnedCount, inProgressCount } =
    useCompanyCredentials(companyProfileId);

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
        <div className="flex items-center gap-2">
          <Award className="h-4 w-4 text-primary" />
          <h3 className="font-semibold">Your credentials</h3>
        </div>
        <div className="text-xs text-muted-foreground">
          {earnedCount} earned · {inProgressCount} in progress
        </div>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Claim credentials as you earn them. Earned badges appear on your profile.
      </p>
      <div className="flex flex-wrap gap-2">
        {CREDENTIAL_CATALOG.map((def) => (
          <CredentialPill
            key={def.key}
            def={def}
            row={byKey.get(def.key)}
            onSet={(s) => setStatus(def.key, s)}
          />
        ))}
      </div>
    </div>
  );
};

export default CredentialsCard;